import { msg } from '@lingui/core/macro';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export type AgreementFromGetMany = Endpoints['GET /agreements']['response']['agreements'][number];

export type AgreementFromGetById = Endpoints['GET /agreements/:id']['response'];

export const useAgreements = (
  params?: Endpoints['GET /agreements']['query'],
  options: {
    enabled?: boolean;
    keepPreviousData?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /agreements']>();

  return useQuery({
    queryKey: ['agreements', params],
    queryFn: () => fetch('/agreements', params),
    enabled: options.enabled !== false,
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined,
  });
};

export const useAgreement = (
  id: string | undefined,
  options: {
    enabled?: boolean;
  } = {}
) => {
  const fetch = useFetch<Endpoints['GET /agreements/:id']>();

  return useQuery({
    queryKey: ['agreement', id],
    queryFn: () => fetch(`/agreements/${id}`),
    enabled: !!id && options.enabled !== false,
  });
};

export const useUpdateAgreement = (agreementId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /agreements/:id']>();

  return useMutation({
    mutationFn: (body: Endpoints['PATCH /agreements/:id']['body']) =>
      fetch(`/agreements/${agreementId}`, undefined, { method: 'PATCH', body }),
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['agreement', agreementId] }),
        queryClient.invalidateQueries({ queryKey: ['agreements'] }),
      ]);
    },
  });
};

export const useDeleteAgreement = (agreementId: string) => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /agreements/:id']>();

  return useMutation({
    mutationFn: () => fetch(`/agreements/${agreementId}`, undefined, { method: 'DELETE' }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agreements'] });
    },
  });
};

export const useCreateAgreement = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /agreements']>();

  return useMutation({
    mutationFn: (body: Endpoints['POST /agreements']['body']) =>
      fetch('/agreements', undefined, { method: 'POST', body }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['agreements'] });
    },
  });
};

export const AGREEMENT_TYPE_LABELS = [
  {
    id: 'price_catalog' as const,
    label: msg({
      id: 'settings.organization.entities.agreements.filters.type.price_catalog',
      message: 'Price catalog',
    }),
  },
  {
    id: 'transit' as const,
    label: msg({
      id: 'settings.organization.entities.agreements.filters.type.transit',
      message: 'Transit',
    }),
  },
  {
    id: 'after_sales' as const,
    label: msg({
      id: 'settings.organization.entities.agreements.filters.type.after_sales',
      message: 'After-sales',
    }),
  },
];

export const AGREEMENT_TYPE_INFO = {
  price_catalog: {
    label: msg({
      id: 'settings.organization.entities.agreements.type.price_catalog',
      message: 'Price catalog',
    }),
    badgeColor: 'secondary' as const,
  },
  transit: {
    label: msg({
      id: 'settings.organization.entities.agreements.type.transit',
      message: 'Transit',
    }),
    badgeColor: 'warning' as const,
  },
  after_sales: {
    label: msg({
      id: 'settings.organization.entities.agreements.type.after_sales',
      message: 'After-sales',
    }),
    badgeColor: 'primary' as const,
  },
} as const;

export const AMOUNT_TO_CONSIDER = {
  amount: msg({
    id: 'settings.organization.entities.agreements.detail.table.amount',
    message: 'Amount',
  }),
  amountBeforeTaxes: msg({
    id: 'settings.organization.entities.agreements.detail.table.amount-before-taxes',
    message: 'Amount w/o tax',
  }),
};
